const AVAILABILITY_PARAM = 'filter.v.availability';
const AVAILABILITY_VALUE = '1';

let requestController = null;
let state = null;

function stockFilterIsActive(url) {
  return url.searchParams.get(AVAILABILITY_PARAM) === AVAILABILITY_VALUE;
}

function getStateKey(url) {
  const keyUrl = new URL(url);

  keyUrl.searchParams.delete('section_id');
  keyUrl.searchParams.delete('page');
  keyUrl.hash = '';

  return keyUrl.toString();
}

function createState(url, pageSize) {
  const baseUrl = new URL(url);
  const sectionId = baseUrl.searchParams.get('section_id');

  baseUrl.searchParams.delete('page');

  return {
    key: getStateKey(url),
    baseUrl,
    sectionId,
    pageSize,
    nextSourcePage: 1,
    sourcePages: new Map(),
    validCards: [],
    exhausted: false
  };
}

function parseSection(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');

  return doc.querySelector('.shopify-section');
}

async function fetchSourcePage(page, signal) {
  if (state.sourcePages.has(page)) {
    return state.sourcePages.get(page);
  }

  const url = new URL(state.baseUrl);

  url.searchParams.set('page', String(page));
  url.searchParams.set('section_id', state.sectionId);

  const response = await fetch(url.toString(), {
    signal
  });

  if (!response.ok) {
    throw new Error(
      `Collection section request failed: ${response.status}`
    );
  }

  const html = await response.text();
  const section = parseSection(html);

  if (!section) {
    throw new Error(
      'Collection section was not found in response.'
    );
  }

  const productList = section.querySelector('product-list');

  const cards = productList
    ? Array.from(productList.children).filter((element) =>
        element.matches('product-card')
      )
    : [];

  const validCards = cards
    .filter(
      (card) =>
        card.getAttribute(
          'data-combined-listing-available'
        ) !== 'false'
    )
    .map((card) => card.outerHTML);

  const result = {
    html,
    validCards,
    hasNextPage: Boolean(
      section.querySelector('.pagination a[rel="next"]')
    )
  };

  state.sourcePages.set(page, result);

  return result;
}

async function ensureValidProducts(targetCount, signal) {
  while (
    state.validCards.length < targetCount &&
    !state.exhausted
  ) {
    const page = state.nextSourcePage;

    const sourcePage = await fetchSourcePage(
      page,
      signal
    );

    state.validCards.push(
      ...sourcePage.validCards
    );

    state.nextSourcePage += 1;

    state.exhausted =
      !sourcePage.hasNextPage;
  }
}

function getOpenFilterNames(section) {
  return new Set(
    Array.from(
      section.querySelectorAll(
        'facets-form details[open] input[name*="filter."]'
      ),
      (input) => input.name
    )
  );
}

function restoreOpenFilters(
  section,
  openFilterNames
) {
  section
    .querySelectorAll('facets-form details')
    .forEach((details) => {
      const inputNames = new Set(
        Array.from(
          details.querySelectorAll(
            'input[name*="filter."]'
          ),
          (input) => input.name
        )
      );

      details.open = Array.from(
        inputNames
      ).some((name) =>
        openFilterNames.has(name)
      );
    });
}

function replaceCountInText(
  element,
  count
) {
  if (!element) return;

  const formattedCount =
    new Intl.NumberFormat(
      document.documentElement.lang || 'en'
    ).format(count);

  element.textContent =
    element.textContent.replace(
      /\d[\d,.]*/,
      formattedCount
    );
}

function updateExactCount(
  section,
  count
) {
  section
    .querySelectorAll(
      '[data-custom-stock-count-label]'
    )
    .forEach((element) => {
      replaceCountInText(
        element,
        count
      );
    });

  section
    .querySelectorAll(
      '[data-custom-stock-count-value]'
    )
    .forEach((element) => {
      element.textContent =
        new Intl.NumberFormat(
          document.documentElement.lang || 'en'
        ).format(count);
    });
}

function trimPagination(
  section,
  currentPage,
  totalPages
) {
  const pagination =
    section.querySelector('.pagination');

  if (!pagination) return;

  if (totalPages <= 1) {
    pagination.remove();
    return;
  }

  pagination
    .querySelectorAll('.pagination__link')
    .forEach((link) => {
      const pageNumber =
        Number.parseInt(
          link.textContent.trim(),
          10
        );

      if (
        Number.isFinite(pageNumber) &&
        pageNumber > totalPages
      ) {
        (
          link.closest('facet-link') ||
          link
        ).remove();
      }
    });

  if (currentPage >= totalPages) {
    const next =
      pagination.querySelector(
        'a[rel="next"]'
      );

    if (next) {
      (
        next.closest('facet-link') ||
        next
      ).remove();
    }
  }
}

function renderEmptyState(section) {
  const results =
    section.querySelector(
      '.collection__results'
    );

  const productList =
    results?.querySelector(
      'product-list'
    );

  const template =
    section.querySelector(
      'template[data-custom-stock-empty-state]'
    );

  productList?.remove();

  section
    .querySelector('.pagination')
    ?.remove();

  if (results && template) {
    results.append(
      template.content.cloneNode(true)
    );
  }
}

function updateHistory(
  url,
  ignoreUrlSearch = false
) {
  const browserUrl =
    new URL(url);

  browserUrl.searchParams.delete(
    'section_id'
  );

  browserUrl.hash = '';

  if (ignoreUrlSearch) {
    browserUrl.search = '';
  }

  history.replaceState(
    {},
    '',
    browserUrl.toString()
  );
}

async function renderStockFilteredCollection(
  eventDetail,
  requestedUrl,
  { scroll = true } = {}
) {
  const liveSection =
    document.getElementById(
      `shopify-section-${requestedUrl.searchParams.get(
        'section_id'
      )}`
    );

  if (!liveSection) return;

  requestController?.abort();

  requestController =
    new AbortController();

  const { signal } =
    requestController;

  const pageSize = Number(
    liveSection.querySelector('#collection')
      ?.dataset.productsPerPage || 24
  );

  const stateKey =
    getStateKey(requestedUrl);

  if (
    !state ||
    state.key !== stateKey
  ) {
    state = createState(
      requestedUrl,
      pageSize
    );
  }

  let visualPage = Math.max(
    1,
    Number.parseInt(
      requestedUrl.searchParams.get(
        'page'
      ) || '1',
      10
    )
  );

  const openFilterNames =
    getOpenFilterNames(liveSection);

  const focusedId =
    document.activeElement?.id;

  document.documentElement.dispatchEvent(
    new CustomEvent(
      'theme:loading:start',
      {
        bubbles: true
      }
    )
  );

  try {
    await ensureValidProducts(
      visualPage * state.pageSize,
      signal
    );

    if (state.exhausted) {
      const exactPages = Math.max(
        1,
        Math.ceil(
          state.validCards.length /
            state.pageSize
        )
      );

      if (visualPage > exactPages) {
        visualPage = exactPages;

        requestedUrl.searchParams.set(
          'page',
          String(visualPage)
        );
      }
    }

    const scaffoldPage =
      state.sourcePages.get(
        visualPage
      ) ||
      state.sourcePages.get(1);

    if (!scaffoldPage) {
      throw new Error(
        'No source collection page available.'
      );
    }

    const newSection =
      parseSection(
        scaffoldPage.html
      );

    const start =
      (visualPage - 1) *
      state.pageSize;

    const end =
      start + state.pageSize;

    const pageCards =
      state.validCards.slice(
        start,
        end
      );

    const productList =
      newSection.querySelector(
        'product-list'
      );

    if (productList) {
      productList.innerHTML =
        pageCards.join('');
    }

    if (state.exhausted) {
      const exactCount =
        state.validCards.length;

      const exactPages =
        Math.ceil(
          exactCount /
            state.pageSize
        );

      updateExactCount(
        newSection,
        exactCount
      );

      if (exactCount === 0) {
        renderEmptyState(
          newSection
        );
      } else {
        trimPagination(
          newSection,
          visualPage,
          exactPages
        );
      }
    }

    restoreOpenFilters(
      newSection,
      openFilterNames
    );

    updateHistory(
      requestedUrl,
      eventDetail.ignoreUrlSearch
    );

    liveSection.replaceChildren(
      ...document.importNode(
        newSection,
        true
      ).childNodes
    );

    if (
      focusedId &&
      document.getElementById(
        focusedId
      )
    ) {
      document
        .getElementById(focusedId)
        .focus();
    }

    if (
      scroll &&
      eventDetail.scrollTo
    ) {
      requestAnimationFrame(() => {
        liveSection
          .querySelector(
            eventDetail.scrollTo
          )
          ?.scrollIntoView({
            block: 'start',
            behavior: 'smooth'
          });
      });
    }
  } catch (error) {
    if (
      error.name !== 'AbortError'
    ) {
      console.error(
        '[combined listing stock filter]',
        error
      );
    }
  } finally {
    if (!signal.aborted) {
      document.documentElement.dispatchEvent(
        new CustomEvent(
          'theme:loading:end',
          {
            bubbles: true
          }
        )
      );
    }
  }
}

document.body.addEventListener(
  'facet:update',
  (event) => {
    const url = new URL(
      event.detail.url,
      window.location.origin
    );

    const sectionId =
      url.searchParams.get(
        'section_id'
      );

    const targetSection =
      sectionId
        ? document.getElementById(
            `shopify-section-${sectionId}`
          )
        : null;

    const supportsCustomStockPagination =
      Boolean(
        targetSection?.querySelector(
          '[data-custom-stock-pagination]'
        )
      );

    if (
      !stockFilterIsActive(url) ||
      !supportsCustomStockPagination
    ) {
      if (!stockFilterIsActive(url)) {
        state = null;
      }

      return;
    }

    event.stopPropagation();

    renderStockFilteredCollection(
      event.detail,
      url
    );
  }
);

const initialUrl =
  new URL(window.location.href);

if (
  stockFilterIsActive(initialUrl)
) {
  const section =
    document.querySelector(
      '.shopify-section--main-collection'
    );

  if (section?.id) {
    const sectionId =
      section.id.replace(
        'shopify-section-',
        ''
      );

    initialUrl.searchParams.set(
      'section_id',
      sectionId
    );

    renderStockFilteredCollection(
      {
        url: initialUrl,
        scrollTo: null,
        ignoreUrlSearch: false
      },
      initialUrl,
      {
        scroll: false
      }
    );
  }
}